/**
 * Operation Panel Component
 * Left panel with mode tabs, input controls, configuration, and execute button
 * Matches the old admin playground layout
 */

import { useState, useCallback, useEffect } from 'react';
import { Wand2, Edit3, Layers, Bot, Keyboard, X, Upload, Cpu, Star } from 'lucide-react';
import { ImageUpload, type UploadedImage } from './ImageUpload';
import { DynamicConfigSelector } from './DynamicConfigSelector';
import { SystemPromptPopover } from '@/components/common';
import {
  useGenerateImageV2,
  useEditImageV2,
  useFuseImagesV2,
  useLuckyCharmGenerate,
  usePromptAssist,
  usePromptAssistTemplate,
  useImageModels,
  useDynamicImageConfig,
  usePlaygroundAgents,
  useLuckyCharmThemes,
  useLuckyCharmImages,
  useLuckyCharmFonts,
} from '../hooks/usePlayground';
import { useOutputSchemasForAgent } from '@/features/system-settings/hooks/useOutputSchemas';
import type { LuckyCharmInputMode, EmotionTrend } from '@/types/playground';
import { useChatModelsQuery } from '@/lib/queries';
import type { OperationMode } from '@/pages/PlaygroundPage';
import emojiRegex from 'emoji-regex';

/**
 * Strip emoji characters from text (fonts used in lucky charm don't support emojis)
 * Uses emoji-regex package for comprehensive emoji detection
 */
const stripEmojis = (text: string): string => {
  const regex = emojiRegex();
  return text.replace(regex, '').trim();
};

interface SelectedImage {
  id: string;
  image_url: string;
  source: 'history' | 'library' | 'upload';
  base64?: string;
}

interface OperationPanelProps {
  operationMode: OperationMode;
  onModeChange: (mode: OperationMode) => void;
  selectedImageForEdit: SelectedImage | null;
  onSelectImageForEdit: (image: SelectedImage | null) => void;
  selectedImagesForFuse: SelectedImage[];
  onSelectImagesForFuse: (images: SelectedImage[]) => void;
}

type InputMode = 'agent' | 'raw' | 'manual';

const MODE_INFO = {
  generate: {
    icon: Wand2,
    title: '生成图像',
    description: '根据文字描述生成全新的图像',
    buttonColor: 'bg-green-600 hover:bg-green-700',
  },
  edit: {
    icon: Edit3,
    title: '编辑图像',
    description: '对已有图像进行修改、调整或增强',
    buttonColor: 'bg-blue-600 hover:bg-blue-700',
  },
  fuse: {
    icon: Layers,
    title: '融合图像',
    description: '将多张图像融合成一张新图像',
    buttonColor: 'bg-purple-600 hover:bg-purple-700',
  },
  lucky_charm: {
    icon: Star,
    title: '幸运符',
    description: '生成个性化运势幸运符图片',
    buttonColor: 'bg-amber-600 hover:bg-amber-700',
  },
};

export function OperationPanel({
  operationMode,
  onModeChange,
  selectedImageForEdit,
  onSelectImageForEdit,
  selectedImagesForFuse,
  onSelectImagesForFuse,
}: OperationPanelProps) {
  const [inputMode, setInputMode] = useState<InputMode>('manual');
  const [userPromptForAgent, setUserPromptForAgent] = useState('');
  const [promptText, setPromptText] = useState('');
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  // Agent mode state
  const [selectedAgentType, setSelectedAgentType] = useState<string>('');
  const [selectedSchemaKey, setSelectedSchemaId] = useState<string | null>(null);
  // Raw LLM mode state
  const [selectedRawModel, setSelectedRawModel] = useState<string>('gpt-4o-mini');
  const [rawTemperature, setRawTemperature] = useState<number>(0.7);
  // Store last response's prompts for transparency display
  const [lastSystemPrompt, setLastSystemPrompt] = useState<string | undefined>();
  const [lastUserPrompt, setLastUserPrompt] = useState<string | undefined>();

  // Lucky Charm mode state
  const [luckyCharmInputMode, setLuckyCharmInputMode] = useState<LuckyCharmInputMode>('agent');
  const [luckyCharmPhrase, setLuckyCharmPhrase] = useState('');
  const [luckyCharmAgentType, setLuckyCharmAgentType] = useState<string>('');
  const [luckyCharmExtraPrompt, setLuckyCharmExtraPrompt] = useState('');
  const [luckyCharmLLMModel, setLuckyCharmLLMModel] = useState<string>('');
  const [luckyCharmLLMTemperature, setLuckyCharmLLMTemperature] = useState<number>(0.7);
  const [luckyCharmChartId, setLuckyCharmChartId] = useState('');
  const [luckyCharmEmotionTrend, setLuckyCharmEmotionTrend] = useState<EmotionTrend>('positive');
  const [luckyCharmThemeId, setLuckyCharmThemeId] = useState<string>('');
  const [luckyCharmBackgroundId, setLuckyCharmBackgroundId] = useState('');
  const [luckyCharmFontId, setLuckyCharmFontId] = useState<number | undefined>();
  const [generatingLuckyCharmPhrase, setGeneratingLuckyCharmPhrase] = useState(false);

  // Fetch Lucky Charm assets (themes, images and fonts)
  const { data: luckyCharmThemes = [], isLoading: themesLoading } = useLuckyCharmThemes();
  const { data: luckyCharmImages = [], isLoading: imagesLoading } = useLuckyCharmImages(luckyCharmThemeId || undefined);
  const { data: luckyCharmFontsData, isLoading: fontsLoading } = useLuckyCharmFonts();
  const luckyCharmFonts = luckyCharmFontsData?.fonts || [];
  const defaultFontId = luckyCharmFontsData?.defaultFontId;

  // Auto-select default font when fonts load
  useEffect(() => {
    if (defaultFontId != null && luckyCharmFontId === undefined) {
      setLuckyCharmFontId(defaultFontId);
    }
  }, [defaultFontId, luckyCharmFontId]);

  // Fetch agents list
  const { data: agents = [], isLoading: agentsLoading } = usePlaygroundAgents();

  // Fetch output schemas for selected agent from centralized API
  const { data: schemasData, isLoading: schemasLoading } = useOutputSchemasForAgent(
    selectedAgentType || undefined
  );
  const outputSchemas = schemasData?.schemas || [];

  // Reset schema selection when agent changes
  useEffect(() => {
    // Reset schema when agent changes
    setSelectedSchemaId(null);
  }, [selectedAgentType]);

  // Fetch chat models for Raw LLM mode (from LiteLLM server)
  const { data: chatModels = [], isLoading: chatModelsLoading } = useChatModelsQuery();

  // Fetch prompt assist template for transparency
  const { data: promptTemplate } = usePromptAssistTemplate();

  // Fetch available image models
  const { data: modelsData, isLoading: modelsLoading } = useImageModels();
  const models = modelsData?.models || [];

  // Auto-select first agent when agents load
  useEffect(() => {
    if (agents.length > 0 && !selectedAgentType) {
      console.log('[OperationPanel] Auto-selecting first agent:', agents[0].agent_type);
      setSelectedAgentType(agents[0].agent_type);
    }
  }, [agents, selectedAgentType]);

  // Auto-select first chat model when chat models load
  useEffect(() => {
    if (chatModels.length > 0 && !selectedRawModel) {
      console.log('[OperationPanel] Auto-selecting first chat model:', chatModels[0].id);
      setSelectedRawModel(chatModels[0].id);
    }
  }, [chatModels, selectedRawModel]);

  // Log current selected agent for debugging
  useEffect(() => {
    console.log('[OperationPanel] selectedAgentType changed to:', selectedAgentType);
  }, [selectedAgentType]);

  // Dynamic config management using API models
  const {
    config,
    currentModel,
    setModel,
    setSize,
    setAspectRatio,
    setQuality,
    setWebSearch,
    setBackground,
  } = useDynamicImageConfig(models, operationMode === 'lucky_charm' ? 'generate' : operationMode);

  // Mutations
  const generateMutation = useGenerateImageV2();
  const editMutation = useEditImageV2();
  const fuseMutation = useFuseImagesV2();
  const luckyCharmMutation = useLuckyCharmGenerate();
  const promptAssistMutation = usePromptAssist();

  const maxFuseImages = currentModel?.max_images_fuse || 4;

  // Handle uploaded images for edit mode
  const handleEditImageUpload = useCallback((images: UploadedImage[]) => {
    if (images.length > 0) {
      const img = images[0];
      onSelectImageForEdit({
        id: img.id,
        image_url: img.preview,
        source: 'upload',
        base64: img.base64,
      });
    }
  }, [onSelectImageForEdit]);

  // Handle uploaded images for fuse mode
  const handleFuseImageUpload = useCallback((images: UploadedImage[]) => {
    const newImages = images.map((img) => ({
      id: img.id,
      image_url: img.preview,
      source: 'upload' as const,
      base64: img.base64,
    }));
    onSelectImagesForFuse([...selectedImagesForFuse, ...newImages].slice(0, maxFuseImages));
  }, [selectedImagesForFuse, onSelectImagesForFuse, maxFuseImages]);

  // Generate prompt with Agent (uses Agent's personality)
  const handleGeneratePromptWithAgent = async () => {
    const agentToUse = selectedAgentType;

    console.log('[OperationPanel] Agent Mode - generating with:', agentToUse, 'schema:', selectedSchemaKey);

    if (!userPromptForAgent.trim()) {
      console.warn('[OperationPanel] User prompt is empty');
      return;
    }
    if (!agentToUse) {
      console.warn('[OperationPanel] No agent selected');
      alert('请先选择一个 Agent');
      return;
    }

    setGeneratingPrompt(true);
    try {
      const response = await promptAssistMutation.mutateAsync({
        user_input: userPromptForAgent,
        language: 'zh',
        agentType: agentToUse,
        operationMode: operationMode as 'generate' | 'edit' | 'fuse',
        output_schema_key: selectedSchemaKey || undefined,
      });
      setPromptText(response.prompt);
      console.log('[OperationPanel] Agent Mode - prompt generated, mode:', response.mode, 'schema applied:', !!selectedSchemaKey);
    } catch (error) {
      console.error('[OperationPanel] Failed to generate prompt:', error);
      const errorMessage = error instanceof Error ? error.message : '生成失败，请稍后重试';
      alert(`生成提示词失败: ${errorMessage}`);
    } finally {
      setGeneratingPrompt(false);
    }
  };

  // Generate prompt with Raw LLM (direct API with custom system prompt)
  const handleGeneratePromptWithRawLLM = async () => {
    console.log('[OperationPanel] Raw Mode - generating with model:', selectedRawModel);

    if (!userPromptForAgent.trim()) {
      console.warn('[OperationPanel] User prompt is empty');
      return;
    }

    setGeneratingPrompt(true);
    try {
      const response = await promptAssistMutation.mutateAsync({
        user_input: userPromptForAgent,
        language: 'zh',
        model: selectedRawModel,
        temperature: rawTemperature,
        operationMode: operationMode as 'generate' | 'edit' | 'fuse',
      });
      setPromptText(response.prompt);
      // Save prompts for transparency display
      setLastSystemPrompt(response.system_prompt);
      setLastUserPrompt(response.user_prompt);
      console.log('[OperationPanel] Raw Mode - prompt generated, tokens:', response.tokens_used);
    } catch (error) {
      console.error('[OperationPanel] Failed to generate prompt:', error);
      const errorMessage = error instanceof Error ? error.message : '生成失败，请稍后重试';
      alert(`生成提示词失败: ${errorMessage}`);
    } finally {
      setGeneratingPrompt(false);
    }
  };

  // Generate Lucky Charm phrase with Agent
  const handleGenerateLuckyCharmPhraseWithAgent = async () => {
    if (!luckyCharmAgentType) {
      alert('请先选择一个 Agent');
      return;
    }

    setGeneratingLuckyCharmPhrase(true);
    try {
      // Build prompt for agent
      let prompt = luckyCharmExtraPrompt || '请生成一句简短的幸运符运势文案。';
      if (luckyCharmEmotionTrend) {
        prompt += `\n情绪趋势: ${luckyCharmEmotionTrend}`;
      }

      // Use prompt assist API with agent mode and output schema
      const response = await promptAssistMutation.mutateAsync({
        user_input: prompt,
        language: 'zh',
        agentType: luckyCharmAgentType,
        operationMode: 'generate',
        output_schema_key: 'lucky_charm',  // Use lucky_charm output schema
      });

      // Extract phrase from response and strip emojis (fonts don't support them)
      const rawPhrase = response.prompt.trim().replace(/^["'"]|["'"]$/g, '');
      const phrase = stripEmojis(rawPhrase);
      setLuckyCharmPhrase(phrase);
      console.log('[OperationPanel] Lucky Charm Agent generated phrase:', phrase);
    } catch (error) {
      console.error('[OperationPanel] Failed to generate lucky charm phrase:', error);
      const errorMessage = error instanceof Error ? error.message : '生成失败，请稍后重试';
      alert(`生成文案失败: ${errorMessage}`);
    } finally {
      setGeneratingLuckyCharmPhrase(false);
    }
  };

  // Generate Lucky Charm phrase with LLM
  const handleGenerateLuckyCharmPhraseWithLLM = async () => {
    if (!luckyCharmLLMModel) {
      alert('请先选择一个 LLM 模型');
      return;
    }
    if (!luckyCharmExtraPrompt?.trim()) {
      alert('请输入额外提示词');
      return;
    }

    setGeneratingLuckyCharmPhrase(true);
    try {
      // Build prompt
      let userInput = luckyCharmExtraPrompt;
      if (luckyCharmEmotionTrend) {
        userInput += `\n情绪趋势: ${luckyCharmEmotionTrend}`;
      }

      // Use prompt assist API with raw LLM mode and custom system prompt
      const response = await promptAssistMutation.mutateAsync({
        user_input: userInput,
        language: 'zh',
        model: luckyCharmLLMModel,
        temperature: luckyCharmLLMTemperature,
        system_prompt: `你是一个专业的运势文案生成器。请根据用户的要求生成简短、积极的幸运符文案。
文案要求：
- 简短精炼（10-30个字）
- 积极向上、鼓舞人心
- 适合印在幸运符图片上
- 只输出文案本身，不要添加任何解释
- 不要使用任何 emoji 表情符号`,
      });

      // Extract phrase from response and strip emojis (fonts don't support them)
      const rawPhrase = response.prompt.trim().replace(/^["'"]|["'"]$/g, '');
      const phrase = stripEmojis(rawPhrase);
      setLuckyCharmPhrase(phrase);
      console.log('[OperationPanel] Lucky Charm LLM generated phrase:', phrase);
    } catch (error) {
      console.error('[OperationPanel] Failed to generate lucky charm phrase:', error);
      const errorMessage = error instanceof Error ? error.message : '生成失败，请稍后重试';
      alert(`生成文案失败: ${errorMessage}`);
    } finally {
      setGeneratingLuckyCharmPhrase(false);
    }
  };

  // Execute operation
  const handleExecute = async () => {
    if (!canProceed()) return;

    try {
      let result;
      if (operationMode === 'generate') {
        result = await generateMutation.mutateAsync({
          prompt: promptText,
          model: config.model,
          size: config.size,
          aspect_ratio: config.aspect_ratio,
          quality: config.quality || undefined,
          output_format: config.output_format,
          web_search: config.web_search,
        });
      } else if (operationMode === 'edit' && selectedImageForEdit) {
        result = await editMutation.mutateAsync({
          prompt: promptText,
          model: config.model,
          size: config.size,
          aspect_ratio: config.aspect_ratio,
          quality: config.quality || undefined,
          output_format: config.output_format,
          source_image: selectedImageForEdit.base64 || selectedImageForEdit.image_url,
        });
      } else if (operationMode === 'fuse') {
        // Collect base64 images, fallback to URLs if base64 not available
        const sourceImages = selectedImagesForFuse.map((img) => img.base64 || img.image_url);
        result = await fuseMutation.mutateAsync({
          prompt: promptText,
          model: config.model,
          size: config.size,
          aspect_ratio: config.aspect_ratio,
          quality: config.quality || undefined,
          output_format: config.output_format,
          source_images: sourceImages,
        });
      } else if (operationMode === 'lucky_charm') {
        // Always use direct mode with the phrase (generated or manually input)
        // Strip emojis before sending - fonts don't support emoji characters
        result = await luckyCharmMutation.mutateAsync({
          mode: 'direct',
          phrase: stripEmojis(luckyCharmPhrase),
          theme_id: luckyCharmThemeId || undefined,
          background_image_id: luckyCharmBackgroundId || undefined,
          font_id: luckyCharmFontId,
        });
      }

      // Check if task failed
      if (result && result.status === 'failed') {
        throw new Error(result.error || '任务执行失败');
      }
    } catch (error) {
      console.error('Operation failed:', error);
      const errorMessage = error instanceof Error ? error.message : '操作失败，请稍后重试';
      alert(`操作失败: ${errorMessage}`);
    }
  };

  // Check if can proceed with operation
  const canProceed = () => {
    if (operationMode === 'lucky_charm') {
      // Always need a phrase to generate lucky charm
      return !!luckyCharmPhrase.trim();
    }
    if (!promptText.trim()) return false;
    if (operationMode === 'edit' && !selectedImageForEdit) return false;
    if (operationMode === 'fuse' && selectedImagesForFuse.length < 2) return false;
    return true;
  };

  const isProcessing = generateMutation.isPending || editMutation.isPending || fuseMutation.isPending || luckyCharmMutation.isPending;
  const modeInfo = MODE_INFO[operationMode];
  const ModeIcon = modeInfo.icon;

  return (
    <div className="p-6">
      {/* Mode Tabs */}
      <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
        {(['generate', 'edit', 'fuse', 'lucky_charm'] as OperationMode[]).map((mode) => {
          const info = MODE_INFO[mode];
          const Icon = info.icon;
          const isActive = operationMode === mode;
          const activeColor = mode === 'generate' ? 'text-green-600'
            : mode === 'edit' ? 'text-blue-600'
            : mode === 'fuse' ? 'text-purple-600'
            : 'text-amber-600';
          const modeLabel = mode === 'generate' ? '生成'
            : mode === 'edit' ? '编辑'
            : mode === 'fuse' ? '融合'
            : '幸运符';
          return (
            <button
              key={mode}
              onClick={() => onModeChange(mode)}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                isActive ? `bg-white shadow ${activeColor}` : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{modeLabel}</span>
            </button>
          );
        })}
      </div>

      {/* Mode Description */}
      <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 text-sm">
          <ModeIcon className="w-4 h-4 text-gray-500" />
          <span className="font-medium">{modeInfo.title}</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">{modeInfo.description}</p>
      </div>

      {/* Image Selection for Edit Mode */}
      {operationMode === 'edit' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            选择要编辑的图像 <span className="text-red-500">*</span>
          </label>
          {selectedImageForEdit ? (
            <div className="relative mb-3">
              <img
                src={selectedImageForEdit.image_url}
                alt="Selected for edit"
                className="w-full h-32 object-cover rounded-lg border-2 border-blue-500"
              />
              <button
                onClick={() => onSelectImageForEdit(null)}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
              >
                <X className="w-3 h-3" />
              </button>
              {selectedImageForEdit.source === 'upload' && (
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  <Upload className="w-3 h-3 inline mr-1" />上传
                </span>
              )}
            </div>
          ) : (
            <ImageUpload
              onUpload={handleEditImageUpload}
              maxImages={1}
              compact={false}
            />
          )}
          <p className="text-xs text-gray-500 mt-2">或从右侧历史/图库中选择</p>
        </div>
      )}

      {/* Image Selection for Fuse Mode */}
      {operationMode === 'fuse' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            选择要融合的图像 (2-{maxFuseImages}张) <span className="text-red-500">*</span>
          </label>
          {selectedImagesForFuse.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mb-3">
              {selectedImagesForFuse.map((img, idx) => (
                <div key={img.id} className="relative">
                  <img
                    src={img.image_url}
                    alt={`Selected ${idx + 1}`}
                    className="w-full h-16 object-cover rounded-lg border-2 border-purple-500"
                  />
                  <button
                    onClick={() => {
                      onSelectImagesForFuse(selectedImagesForFuse.filter((i) => i.id !== img.id));
                    }}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <span className="absolute bottom-1 left-1 bg-purple-600 text-white text-xs px-1 rounded">
                    {idx + 1}
                  </span>
                  {img.source === 'upload' && (
                    <span className="absolute top-0 left-0 bg-orange-500 text-white text-xs px-1 rounded-br">
                      <Upload className="w-2 h-2" />
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {selectedImagesForFuse.length < maxFuseImages && (
            <ImageUpload
              onUpload={handleFuseImageUpload}
              maxImages={maxFuseImages - selectedImagesForFuse.length}
              multiple
              compact={false}
            />
          )}
          <p className="text-xs text-gray-500 mt-2">
            已选择 {selectedImagesForFuse.length} / {maxFuseImages} 张。支持多选，或从右侧历史/图库中选择
          </p>
        </div>
      )}

      {/* Lucky Charm Configuration */}
      {operationMode === 'lucky_charm' && (
        <div className="mb-6 p-4 border border-amber-200 rounded-lg bg-amber-50">
          <h3 className="text-sm font-medium text-amber-800 mb-4 flex items-center gap-2">
            <Star className="w-4 h-4" />
            幸运符配置
          </h3>

          {/* Input Mode Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">生成方式</label>
            <div className="flex gap-2">
              <button
                onClick={() => setLuckyCharmInputMode('agent')}
                className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  luckyCharmInputMode === 'agent'
                    ? 'bg-amber-100 border-amber-500 text-amber-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Bot className="w-4 h-4" />
                <span>Agent</span>
              </button>
              <button
                onClick={() => setLuckyCharmInputMode('llm')}
                className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  luckyCharmInputMode === 'llm'
                    ? 'bg-amber-100 border-amber-500 text-amber-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Cpu className="w-4 h-4" />
                <span>LLM</span>
              </button>
              <button
                onClick={() => setLuckyCharmInputMode('manual')}
                className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  luckyCharmInputMode === 'manual'
                    ? 'bg-amber-100 border-amber-500 text-amber-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Keyboard className="w-4 h-4" />
                <span>手动</span>
              </button>
            </div>
          </div>

          {/* Manual Mode: Phrase Input */}
          {luckyCharmInputMode === 'manual' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                运势文案 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={luckyCharmPhrase}
                onChange={(e) => setLuckyCharmPhrase(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                placeholder="输入运势文案，例如：今日宜静心思考，贵人运旺盛..."
              />
            </div>
          )}

          {/* Agent Mode: Agent Selection + Extra Prompt + Generate Button */}
          {luckyCharmInputMode === 'agent' && (
            <div className="mb-4 space-y-3">
              {/* Agent Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  选择 Agent <span className="text-red-500">*</span>
                  {agentsLoading && <span className="ml-2 text-gray-400 text-xs">加载中...</span>}
                </label>
                <select
                  value={luckyCharmAgentType}
                  onChange={(e) => setLuckyCharmAgentType(e.target.value)}
                  disabled={agentsLoading || agents.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
                >
                  <option value="">选择 Agent...</option>
                  {agents.map((agent) => (
                    <option key={agent.agent_type} value={agent.agent_type}>
                      {agent.name} ({agent.agent_type})
                    </option>
                  ))}
                </select>
              </div>

              {/* Emotion Trend */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">情绪趋势</label>
                <select
                  value={luckyCharmEmotionTrend}
                  onChange={(e) => setLuckyCharmEmotionTrend(e.target.value as EmotionTrend)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="positive">积极 (Positive)</option>
                  <option value="neutral">中性 (Neutral)</option>
                  <option value="negative">消极 (Negative)</option>
                  <option value="mixed">混合 (Mixed)</option>
                </select>
              </div>

              {/* Extra Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  额外提示词 <span className="text-gray-400">(可选)</span>
                </label>
                <textarea
                  value={luckyCharmExtraPrompt}
                  onChange={(e) => setLuckyCharmExtraPrompt(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                  placeholder="给 Agent 的额外提示，例如：突出财运..."
                />
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateLuckyCharmPhraseWithAgent}
                disabled={generatingLuckyCharmPhrase || !luckyCharmAgentType}
                className="w-full px-4 py-2.5 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                <Wand2 className={`w-4 h-4 ${generatingLuckyCharmPhrase ? 'animate-spin' : ''}`} />
                <span>{generatingLuckyCharmPhrase ? '生成中...' : '生成文案'}</span>
              </button>
            </div>
          )}

          {/* LLM Mode: Model Selection + Temperature + Extra Prompt + Generate Button */}
          {luckyCharmInputMode === 'llm' && (
            <div className="mb-4 space-y-3">
              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  选择 LLM 模型 <span className="text-red-500">*</span>
                  {chatModelsLoading && <span className="ml-2 text-gray-400 text-xs">加载中...</span>}
                </label>
                <select
                  value={luckyCharmLLMModel}
                  onChange={(e) => setLuckyCharmLLMModel(e.target.value)}
                  disabled={chatModelsLoading || chatModels.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
                >
                  <option value="">选择模型...</option>
                  {chatModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.id}
                    </option>
                  ))}
                </select>
              </div>

              {/* Temperature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  温度 (Temperature): {luckyCharmLLMTemperature.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={luckyCharmLLMTemperature}
                  onChange={(e) => setLuckyCharmLLMTemperature(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>精确 (0)</span>
                  <span>平衡 (1)</span>
                  <span>创意 (2)</span>
                </div>
              </div>

              {/* Emotion Trend */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">情绪趋势</label>
                <select
                  value={luckyCharmEmotionTrend}
                  onChange={(e) => setLuckyCharmEmotionTrend(e.target.value as EmotionTrend)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="positive">积极 (Positive)</option>
                  <option value="neutral">中性 (Neutral)</option>
                  <option value="negative">消极 (Negative)</option>
                  <option value="mixed">混合 (Mixed)</option>
                </select>
              </div>

              {/* Extra Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  额外提示词 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={luckyCharmExtraPrompt}
                  onChange={(e) => setLuckyCharmExtraPrompt(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                  placeholder="生成幸运符文案的提示词，例如：生成一句积极正面的运势文案..."
                />
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateLuckyCharmPhraseWithLLM}
                disabled={generatingLuckyCharmPhrase || !luckyCharmLLMModel || !luckyCharmExtraPrompt?.trim()}
                className="w-full px-4 py-2.5 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                <Wand2 className={`w-4 h-4 ${generatingLuckyCharmPhrase ? 'animate-spin' : ''}`} />
                <span>{generatingLuckyCharmPhrase ? '生成中...' : '生成文案'}</span>
              </button>
            </div>
          )}

          {/* Final Phrase (editable, shown for Agent/LLM modes only) */}
          {luckyCharmInputMode !== 'manual' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                运势文案 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={luckyCharmPhrase}
                onChange={(e) => setLuckyCharmPhrase(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 bg-white"
                placeholder="点击上方「生成文案」按钮，或直接输入文案..."
              />
              <p className="text-xs text-gray-500 mt-1">生成后可以编辑修改</p>
            </div>
          )}

          {/* Common Options */}
          <div className="border-t border-amber-200 pt-4 mt-4">
            <h4 className="text-xs font-medium text-amber-700 mb-3">自定义选项 (可选)</h4>
            <div className="space-y-3">
              {/* Chart ID (for context in agent/llm modes) */}
              {(luckyCharmInputMode === 'agent' || luckyCharmInputMode === 'llm') && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    星盘 ID
                  </label>
                  <input
                    type="text"
                    value={luckyCharmChartId}
                    onChange={(e) => setLuckyCharmChartId(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-amber-500 focus:border-amber-500"
                    placeholder="留空使用用户默认星盘"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">
                    提供星盘数据作为生成运势文案的上下文
                  </p>
                </div>
              )}

              {/* Theme/Pack Selector */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  背景套件
                  {themesLoading && <span className="ml-1 text-gray-400">(加载中...)</span>}
                </label>
                <select
                  value={luckyCharmThemeId}
                  onChange={(e) => {
                    setLuckyCharmThemeId(e.target.value);
                    setLuckyCharmBackgroundId(''); // Reset background when theme changes
                  }}
                  disabled={themesLoading}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
                >
                  <option value="">🎨 所有套件</option>
                  {luckyCharmThemes.map((theme) => (
                    <option key={theme.id} value={theme.id} title={theme.description}>
                      {theme.display_name} ({theme.image_count}张)
                    </option>
                  ))}
                </select>
                {luckyCharmThemeId && luckyCharmThemes.find(t => t.id === luckyCharmThemeId)?.description && (
                  <p className="mt-0.5 text-xs text-gray-400">
                    {luckyCharmThemes.find(t => t.id === luckyCharmThemeId)?.description}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Background Image Selector */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    背景图
                    {imagesLoading && <span className="ml-1 text-gray-400">(加载中...)</span>}
                  </label>
                  <select
                    value={luckyCharmBackgroundId}
                    onChange={(e) => setLuckyCharmBackgroundId(e.target.value)}
                    disabled={imagesLoading}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
                  >
                    <option value="">🎲 随机{luckyCharmThemeId ? '(当前套件)' : ''}</option>
                    {luckyCharmImages.map((img) => (
                      <option key={img.id} value={img.id} title={img.filename}>
                        {img.display_name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Font Selector */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    字体
                    {fontsLoading && <span className="ml-1 text-gray-400">(加载中...)</span>}
                  </label>
                  <select
                    value={luckyCharmFontId ?? ''}
                    onChange={(e) => setLuckyCharmFontId(e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={fontsLoading}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
                  >
                    <option value="">默认字体</option>
                    {luckyCharmFonts.map((font) => (
                      <option key={font.id} value={font.id} title={font.description}>
                        {font.display_name || font.name}{font.is_default ? ' ✓' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prompt Input Section - Grouped (Hidden for Lucky Charm mode) */}
      {operationMode !== 'lucky_charm' && (
      <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-white">
        {/* Input Mode Toggle */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">描述输入方式</label>
          <div className="flex gap-2">
            <button
              onClick={() => setInputMode('agent')}
              className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                inputMode === 'agent'
                  ? 'bg-primary-100 border-primary-500 text-primary-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Bot className="w-4 h-4" />
              <span>Agent</span>
            </button>
            <button
              onClick={() => setInputMode('raw')}
              className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                inputMode === 'raw'
                  ? 'bg-primary-100 border-primary-500 text-primary-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Cpu className="w-4 h-4" />
              <span>LLM</span>
            </button>
            <button
              onClick={() => setInputMode('manual')}
              className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                inputMode === 'manual'
                  ? 'bg-primary-100 border-primary-500 text-primary-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Keyboard className="w-4 h-4" />
              <span>手动</span>
            </button>
          </div>
        </div>

        {/* Agent-Assisted Input */}
        {inputMode === 'agent' && (
          <div className="mb-4 space-y-4">
            {/* Agent Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                选择 Agent
                {agentsLoading && <span className="ml-2 text-gray-400 text-xs">加载中...</span>}
              </label>
              <select
                value={selectedAgentType}
                onChange={(e) => {
                  const newValue = e.target.value;
                  console.log('[OperationPanel] Agent selected:', newValue);
                  setSelectedAgentType(newValue);
                }}
                disabled={agentsLoading || agents.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {agents.length === 0 && !agentsLoading && (
                  <option value="">无可用 Agent</option>
                )}
                {agents.map((agent) => (
                  <option key={agent.agent_type} value={agent.agent_type}>
                    {agent.name} ({agent.agent_type})
                  </option>
                ))}
              </select>
              {selectedAgentType && agents.find(a => a.agent_type === selectedAgentType)?.description && (
                <p className="mt-1 text-xs text-gray-500">
                  {agents.find(a => a.agent_type === selectedAgentType)?.description}
                </p>
              )}
            </div>

            {/* Output Schema Selection (only if agent has schemas) */}
            {selectedAgentType && (outputSchemas.length > 0 || schemasLoading) && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <span>输出格式 (Output Schema)</span>
                  {schemasLoading && <span className="text-gray-400 text-xs">加载中...</span>}
                </label>
                <select
                  value={selectedSchemaKey || 'none'}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedSchemaId(value === 'none' ? null : value);
                  }}
                  disabled={generatingPrompt || schemasLoading || outputSchemas.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="none">无格式约束 (自由输出)</option>
                  {outputSchemas.map((schema) => (
                    <option key={schema.key} value={schema.key}>
                      {schema.name_zh || schema.name} ({schema.key})
                    </option>
                  ))}
                </select>
                {selectedSchemaKey && outputSchemas.find(s => s.key === selectedSchemaKey)?.description && (
                  <p className="text-xs text-gray-500">
                    {outputSchemas.find(s => s.key === selectedSchemaKey)?.description}
                  </p>
                )}
                {!selectedSchemaKey && outputSchemas.length > 0 && (
                  <p className="text-xs text-gray-400">
                    选择格式后，Agent 会按指定的 JSON 结构输出
                  </p>
                )}
              </div>
            )}

            {/* User Guidance for Agent */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">告诉 Agent 你想要什么</label>
              <textarea
                value={userPromptForAgent}
                onChange={(e) => setUserPromptForAgent(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="例如：一个穿着白裙子的女孩在樱花树下..."
              />
            </div>

            {/* Generate Prompt Button */}
            <button
              onClick={handleGeneratePromptWithAgent}
              disabled={generatingPrompt || !userPromptForAgent?.trim() || !selectedAgentType}
              className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              <Wand2 className={`w-4 h-4 ${generatingPrompt ? 'animate-spin' : ''}`} />
              <span>{generatingPrompt ? '生成中...' : '生成描述'}</span>
            </button>
          </div>
        )}

        {/* Raw LLM Mode Input */}
        {inputMode === 'raw' && (
          <div className="mb-4 space-y-4">
            {/* Model Selection */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <span>选择 LLM 模型</span>
                {chatModelsLoading && <span className="text-gray-400 text-xs">加载中...</span>}
                {/* Show prompts used after generation */}
                {(lastSystemPrompt || lastUserPrompt) && (
                  <SystemPromptPopover
                    systemPrompt={lastSystemPrompt}
                    userPrompt={lastUserPrompt}
                    iconSize={14}
                  />
                )}
              </label>
              <select
                value={selectedRawModel}
                onChange={(e) => {
                  const newValue = e.target.value;
                  console.log('[OperationPanel] Raw model selected:', newValue);
                  setSelectedRawModel(newValue);
                }}
                disabled={chatModelsLoading || chatModels.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {chatModels.length === 0 && !chatModelsLoading && (
                  <option value="">无可用模型</option>
                )}
                {chatModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.id}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                使用原始 LLM API 生成提示词，完全控制系统提示
              </p>
            </div>

            {/* System Prompt Template (for transparency) */}
            {promptTemplate && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">System Prompt ({operationMode})</span>
                </div>
                <pre className="text-xs text-gray-600 whitespace-pre-wrap max-h-24 overflow-y-auto">
                  {promptTemplate.system_prompts?.[operationMode]?.['zh'] || '加载中...'}
                </pre>
              </div>
            )}

            {/* Temperature */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                温度 (Temperature): {rawTemperature.toFixed(1)}
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={rawTemperature}
                onChange={(e) => setRawTemperature(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>精确 (0)</span>
                <span>平衡 (1)</span>
                <span>创意 (2)</span>
              </div>
            </div>

            {/* User Input for Raw Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">告诉 LLM 你想要什么</label>
              <textarea
                value={userPromptForAgent}
                onChange={(e) => setUserPromptForAgent(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="例如：一个穿着白裙子的女孩在樱花树下..."
              />
            </div>

            {/* Generate Prompt Button */}
            <button
              onClick={handleGeneratePromptWithRawLLM}
              disabled={generatingPrompt || !userPromptForAgent?.trim() || !selectedRawModel}
              className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              <Wand2 className={`w-4 h-4 ${generatingPrompt ? 'animate-spin' : ''}`} />
              <span>{generatingPrompt ? '生成中...' : '生成描述'}</span>
            </button>

            {/* Show actual User Prompt after generation */}
            {lastUserPrompt && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-blue-600">实际发送的 User Prompt</span>
                </div>
                <pre className="text-xs text-blue-800 whitespace-pre-wrap max-h-24 overflow-y-auto">
                  {lastUserPrompt}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Final Prompt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {operationMode === 'generate' ? '图像描述' : operationMode === 'edit' ? '编辑指令' : '融合指令'}
            <span className="text-red-500"> *</span>
          </label>
          <textarea
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            placeholder={
              operationMode === 'generate'
                ? '描述你想要生成的图像...'
                : operationMode === 'edit'
                ? '描述你想要如何编辑图像...'
                : '描述你想要如何融合这些图像...'
            }
          />
        </div>
      </div>
      )}

      {/* Configuration - Dynamic Model Selection (Hidden for Lucky Charm mode) */}
      {operationMode !== 'lucky_charm' && (
      <div className="mb-4">
        <DynamicConfigSelector
          models={models}
          config={config}
          currentModel={currentModel}
          operationMode={operationMode}
          onModelChange={setModel}
          onSizeChange={setSize}
          onAspectRatioChange={setAspectRatio}
          onQualityChange={setQuality}
          onWebSearchChange={setWebSearch}
          onBackgroundChange={setBackground}
          isLoading={modelsLoading}
        />
      </div>
      )}

      {/* Execute Button */}
      <button
        onClick={handleExecute}
        disabled={isProcessing || !canProceed()}
        className={`w-full px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 text-white ${modeInfo.buttonColor}`}
      >
        {isProcessing ? (
          <>
            <span className="inline-block animate-spin mr-2">⏳</span>
            处理中...
          </>
        ) : (
          <>
            <ModeIcon className="w-4 h-4 inline mr-2" />
            {modeInfo.title}
          </>
        )}
      </button>

      {/* Error Display */}
      {(generateMutation.isError || editMutation.isError || fuseMutation.isError || luckyCharmMutation.isError) && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">
            {(generateMutation.error as Error)?.message ||
              (editMutation.error as Error)?.message ||
              (fuseMutation.error as Error)?.message ||
              (luckyCharmMutation.error as Error)?.message ||
              '操作失败'}
          </p>
        </div>
      )}
    </div>
  );
}
